import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useGoogleLogin } from '@react-oauth/google';
import { handleGoogleLogin, navigate } from "../../App";
import gmailIcon from '../../assets/gmail.png';
import guestIcon from '../../assets/guest.png';

export const LoginForm = ({ onGoogleLogin, onTelegramLogin, onGuestContinue }) => {
  const [selectedLoginMethod, setSelectedLoginMethod] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [clickedOnce, setClickedOnce] = useState(false);

  useEffect(() => {
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(isMobileDevice);
  }, []);

  useEffect(() => {
    if (clickedOnce) {
      const timer = setTimeout(() => setClickedOnce(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [clickedOnce]);

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => { await handleGoogleLogin(tokenResponse.access_token); },
    onError: () => { alert("Google login failed"); },
    scope: 'email profile',
    ux_mode: 'popup',
    prompt: 'select_account',
  });

  const handleDirectRedirect = () => {
    const clientId = '700733605957-2sej2crh17hi5g7ukuma6id6r4thcv1e.apps.googleusercontent.com';
    const redirectUri = encodeURIComponent(window.location.origin + window.location.pathname);
    const scope = encodeURIComponent('email profile');
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=token&prompt=select_account`;
    navigate(authUrl);
  };

  const handleGuest = () => {
    setSelectedLoginMethod('guest');
    onGuestContinue();
  };

  const handleGoogleButtonClick = () => {
    if (isMobile && clickedOnce) {
      handleDirectRedirect();
    } else {
      setClickedOnce(true);
      setSelectedLoginMethod('google');
      googleLogin();
    }
  };

  const isBusy = selectedLoginMethod !== null;

  return (
    <section id="login-form" dir="rtl" className="relative py-14 sm:py-20 px-6 bg-surface-base">
      <div className="w-full max-w-md mx-auto">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <p className="eyebrow text-brand-700 mb-3">המקומית</p>
          <h2 className="display-2 mb-2">בואו נתחיל</h2>
          <p className="text-lead text-ink-muted">בחרו דרך התחברות</p>
        </motion.div>

        {/* Login card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="surface-elevated rounded-2xl p-6 sm:p-8"
        >
          <div className="space-y-3">
            {/* Google */}
            <motion.button
              type="button"
              onClick={handleGoogleButtonClick}
              disabled={isBusy}
              whileHover={!isBusy ? { y: -1 } : {}}
              whileTap={!isBusy ? { scale: 0.99 } : {}}
              className={[
                'w-full flex items-center justify-center gap-3',
                'px-5 py-3.5 rounded-xl border text-base font-semibold',
                'transition-[background-color,box-shadow,border-color] duration-200 ease-smooth',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                selectedLoginMethod === 'google'
                  ? 'bg-surface-muted text-ink-muted border-line cursor-not-allowed opacity-70'
                  : 'bg-surface text-ink border-line hover:border-brand-300 hover:shadow-card',
              ].join(' ')}
            >
              <img src={gmailIcon} alt="" aria-hidden="true" className="w-6 h-6 flex-shrink-0" />
              <span>{selectedLoginMethod === 'google' ? 'מתחבר...' : 'המשך עם Google'}</span>
            </motion.button>

            {/* Divider */}
            <div className="relative flex items-center justify-center py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-line-subtle" />
              </div>
              <span className="relative bg-surface px-3 text-xs text-ink-subtle font-semibold">או</span>
            </div>

            {/* Guest */}
            <motion.button
              type="button"
              onClick={handleGuest}
              disabled={isBusy}
              whileHover={!isBusy ? { y: -1 } : {}}
              whileTap={!isBusy ? { scale: 0.99 } : {}}
              className={[
                'w-full flex items-center justify-center gap-3',
                'px-5 py-3.5 rounded-xl border text-base font-semibold',
                'transition-[background-color,box-shadow,border-color] duration-200 ease-smooth',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                selectedLoginMethod === 'guest'
                  ? 'bg-surface-muted text-ink-muted border-line cursor-not-allowed opacity-70'
                  : 'bg-brand-500 text-ink-inverse border-brand-600 hover:bg-brand-600 hover:shadow-card',
              ].join(' ')}
            >
              <img src={guestIcon} alt="" aria-hidden="true" className="w-6 h-6 flex-shrink-0" />
              <span>{selectedLoginMethod === 'guest' ? 'נכנס...' : 'המשך כאורח'}</span>
            </motion.button>
          </div>

          <p className="mt-6 text-center text-xs text-ink-subtle">
            אנחנו מכבדים את הפרטיות שלכם
          </p>
        </motion.div>
      </div>
    </section>
  );
};
